import type React from "react";

type PartnerHighlight = {
  title: string;
  description: string;
};

type PartnerSpotlightProps = {
  name: string;
  link: string;
  summary: string;
  highlights: PartnerHighlight[];
  cta: { label: string; href: string; isExternal?: boolean };
  isExternal?: boolean;
};

const PartnerSpotlight: React.FC<PartnerSpotlightProps> = ({
  name,
  link,
  summary,
  highlights,
  cta,
  isExternal = true,
}) => {
  return (
    <section className="py-20 text-white bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700">
      <div className="container grid gap-10 lg:grid-cols-[1fr_minmax(0,1fr)] lg:items-center">
        <div className="space-y-6">
          <span className="inline-flex w-fit items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-primary-100">
            Partner spotlight
          </span>
          <h2 className="text-3xl font-semibold sm:text-4xl">
            Working in lockstep with {name}
          </h2>
          <p className="text-base leading-relaxed text-primary-100/90">
            {summary}
          </p>
          <a
            href={link}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noreferrer" : undefined}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white underline decoration-white/60 decoration-2 underline-offset-4 hover:decoration-white"
          >
            Visit {name}
            {isExternal ? (
              <span className="sr-only"> (opens in a new tab)</span>
            ) : null}
          </a>
        </div>

        <div className="p-8 border rounded-3xl border-white/15 bg-white/10 backdrop-blur-sm">
          <ul className="space-y-6 text-left">
            {highlights.map((highlight) => (
              <li key={highlight.title} className="space-y-2">
                <h3 className="text-lg font-semibold text-white">
                  {highlight.title}
                </h3>
                <p className="text-sm leading-relaxed text-primary-100/85">
                  {highlight.description}
                </p>
              </li>
            ))}
          </ul>

          <div className="mt-8">
            <a
              href={cta.href}
              target={cta.isExternal ? "_blank" : undefined}
              rel={cta.isExternal ? "noreferrer" : undefined}
              className="inline-flex items-center btn-primary"
            >
              {cta.label}
              {cta.isExternal ? (
                <span className="sr-only"> (opens in a new tab)</span>
              ) : null}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerSpotlight;
